import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  tournamentSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';

/**
 * Standard-style Cultural Algorithm for discrete service composition.
 *
 * The implementation keeps an explicit belief space with:
 * - situational knowledge: the best accepted individual
 * - normative knowledge: the most frequent service choice per task in the accepted set
 *
 * New offspring are generated in population space, then influenced by the belief
 * space before entering the next generation.
 */

interface BeliefSpace {
  situational: Chromosome | null;
  normative: number[];
}

function cloneChromosome(chromosome: Chromosome): Chromosome {
  return {
    x: [...chromosome.x],
    fitness: chromosome.fitness,
    rwp: chromosome.rwp,
  };
}

function selectParentIndex(
  population: Chromosome[],
  selectionRate: number,
  rng: SeededRNG
): number {
  if (rng.randomChance(selectionRate / 100)) {
    return tournamentSelection(population, rng);
  }

  return rng.randomInt(population.length);
}

function updateBeliefSpace(
  beliefSpace: BeliefSpace,
  acceptedSet: Chromosome[],
  taskCount: number,
  serviceCount: number
): void {
  if (acceptedSet.length === 0) {
    return;
  }

  const bestAccepted = findBestChromosome(acceptedSet);
  beliefSpace.situational = cloneChromosome(bestAccepted);

  const frequencies: number[][] = Array.from({ length: taskCount }, () =>
    new Array(serviceCount).fill(0)
  );

  for (const chromosome of acceptedSet) {
    for (let task = 0; task < taskCount; task++) {
      const serviceId = chromosome.x[task];
      if (serviceId >= 0 && serviceId < serviceCount) {
        frequencies[task][serviceId]++;
      }
    }
  }

  beliefSpace.normative = frequencies.map((taskFrequencies) => {
    let bestService = 0;
    let bestFrequency = taskFrequencies[0];

    for (let serviceId = 1; serviceId < taskFrequencies.length; serviceId++) {
      if (taskFrequencies[serviceId] > bestFrequency) {
        bestFrequency = taskFrequencies[serviceId];
        bestService = serviceId;
      }
    }

    return bestService;
  });
}

function influenceChromosome(
  chromosome: Chromosome,
  beliefSpace: BeliefSpace,
  taskCount: number,
  rng: SeededRNG
): Chromosome {
  const influenced: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  const influenceCount = Math.max(1, Math.min(3, Math.floor(taskCount / 10) || 1));
  const usedIndices = new Set<number>();

  for (let i = 0; i < influenceCount && usedIndices.size < taskCount; i++) {
    let taskIdx = rng.randomInt(taskCount);
    while (usedIndices.has(taskIdx)) {
      taskIdx = rng.randomInt(taskCount);
    }

    usedIndices.add(taskIdx);

    if (beliefSpace.situational && rng.randomChance(0.5)) {
      influenced.x[taskIdx] = beliefSpace.situational.x[taskIdx];
    } else if (beliefSpace.normative.length > taskIdx) {
      influenced.x[taskIdx] = beliefSpace.normative[taskIdx];
    }
  }

  return influenced;
}

/**
 * Main Cultural Algorithm evolution step.
 */
export function evolveCultural(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  beliefSpace: BeliefSpace,
  acceptedCount: number,
  rng: SeededRNG
): void {
  const popSize = population.length;
  const eliteCount = Math.max(1, Math.floor(popSize * 0.1));

  population.sort((a, b) => b.fitness - a.fitness);
  const acceptedSet = population.slice(0, acceptedCount);
  updateBeliefSpace(beliefSpace, acceptedSet, taskCount, serviceCount);

  const newPopulation: Chromosome[] = population
    .slice(0, eliteCount)
    .map(cloneChromosome);

  while (newPopulation.length < popSize) {
    const parent1 = population[selectParentIndex(population, selectionRate, rng)];
    const parent2 = population[selectParentIndex(population, selectionRate, rng)];

    let child1: Chromosome;
    let child2: Chromosome;

    if (taskCount > 1 && rng.randomChance(crossoverRate / 100)) {
      [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);
    } else {
      child1 = { x: [...parent1.x], fitness: 0, rwp: 0 };
      child2 = { x: [...parent2.x], fitness: 0, rwp: 0 };
    }

    child1 = influenceChromosome(child1, beliefSpace, taskCount, rng);
    child2 = influenceChromosome(child2, beliefSpace, taskCount, rng);

    if (rng.randomChance(mutationRate / 100)) {
      child1 = mutate(child1, taskCount, serviceCount, rng);
    }
    if (rng.randomChance(mutationRate / 100)) {
      child2 = mutate(child2, taskCount, serviceCount, rng);
    }

    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);

    newPopulation.push(child1);
    if (newPopulation.length < popSize) {
      newPopulation.push(child2);
    }
  }

  for (let i = 0; i < popSize; i++) {
    population[i] = newPopulation[i];
  }
}

/**
 * Runs Cultural Algorithm for specified generations.
 */
export async function runCulturalAlgorithm(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
  }
): Promise<Chromosome> {
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;

  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount,
    rng
  );

  for (const chromosome of population) {
    calculateChromosomeFitness(chromosome, matrix, taskCount, serviceCount);
  }

  const beliefSpace: BeliefSpace = {
    situational: null,
    normative: new Array(taskCount).fill(0),
  };

  const acceptedCount = Math.max(1, Math.floor(parameters.populationSize * 0.1));
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100;

  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveCultural(
      population,
      taskCount,
      serviceCount,
      matrix,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate,
      beliefSpace,
      acceptedCount,
      rng
    );

    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      (now - lastUpdateTime > UPDATE_INTERVAL) ||
      (gen % 10 === 9) ||
      (gen === parameters.generations - 1);

    if (shouldUpdate) {
      const best = findBestChromosome(population);
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, best.fitness);
      }
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  return findBestChromosome(population);
}
